/* ═══════════════════════════════════════════════════════════════════════════════
   Dashboard filter hooks, separated from the Provider component so the file
   that exports the Provider satisfies react-refresh/only-export-components.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useContext, useMemo } from 'react';
import type { ProjectWithDetails } from '../hooks/useProjects';
import type { DashboardFilterContextValue } from '../types/dashboard-filters';
import { DashboardFilterContext } from './DashboardFilterContextDef';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDashboardFilter(): DashboardFilterContextValue {
  const context = useContext(DashboardFilterContext);
  if (!context) {
    throw new Error(
      'useDashboardFilter must be used within a <DashboardFilterProvider>',
    );
  }
  return context;
}

// ─── Convenience Hooks ───────────────────────────────────────────────────────

/** Returns available builder names extracted from the projects list. */
export function useAvailableBuilders(
  projects: ProjectWithDetails[],
): string[] {
  return useMemo(() => {
    const builders = new Set<string>();
    for (const p of projects) {
      if (p.builder_name) builders.add(p.builder_name);
    }
    return Array.from(builders).sort();
  }, [projects]);
}
